import { useEffect, useRef } from "react";
import { property } from "../data/property";
import { ExternalLink, MapPin } from "lucide-react";

export default function MapSection() {
  const mapEl = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapEl.current || mapInstance.current) return;

      const { lat, lng } = property.coords;
      const map = L.map(mapEl.current, {
        center: [lat, lng],
        zoom: 16,
        scrollWheelZoom: false,
        zoomControl: true,
      });
      mapInstance.current = map;

      L.tileLayer(
        "https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 19,
        }
      ).addTo(map);

      const icon = L.divIcon({
        className: "custom-marker",
        html: `<div style="position:relative;width:44px;height:44px;">
                 <div style="position:absolute;inset:0;border-radius:9999px;background:#4A5D4E;opacity:0.25;animation:pulseMarker 1.8s ease-out infinite;"></div>
                 <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:18px;height:18px;background:#4A5D4E;border:3px solid #FCFAF8;border-radius:9999px;box-shadow:0 4px 14px rgba(0,0,0,0.25);"></div>
               </div>`,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
      });

      L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(
          `<div style="font-family:'Cormorant Garamond',serif;font-size:18px;line-height:1.2;color:#1A1A1A;"><strong>${property.address}</strong></div><div style="font-family:Outfit,sans-serif;font-size:12px;color:#5C5C5C;margin-top:2px;">${property.postalCode} ${property.city}</div>`
        )
        .openPopup();
    })();

    return () => {
      cancelled = true;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  return (
    <section
      id="ligging"
      data-testid="map-section"
      className="bg-brand-bg py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="overline">De ligging</p>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl text-balance">
              Op de kaart, <span className="italic">letterlijk</span> centraal.
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-brand-muted">
            <MapPin size={16} className="text-brand-moss" />
            {property.address}, {property.postalCode} {property.city}
          </div>
        </div>

        <div className="relative">
          <div
            ref={mapEl}
            data-testid="leaflet-map"
            className="h-[460px] w-full border border-brand-border md:h-[560px]"
            style={{ background: "#F2EFE9" }}
          />
          <a
            data-testid="map-google-link"
            href={`https://www.google.nl/maps/place/Kempenaar+29+3961KN+Wijk+bij+Duurstede`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute right-4 top-4 z-[400] inline-flex items-center gap-2 bg-brand-bg/95 px-4 py-2 text-xs font-medium text-brand-ink shadow-md backdrop-blur transition-colors hover:bg-white"
          >
            Open in Google Maps
            <ExternalLink size={12} />
          </a>
        </div>

        <p className="mt-6 text-sm text-brand-muted">
          Op loopafstand van winkels, scholen, sportvoorzieningen en de historische binnenstad. Op de fiets in enkele minuten bij de Lek.
        </p>
      </div>

      <style>{`
        @keyframes pulseMarker {
          0% { transform: scale(0.6); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          background: #FCFAF8 !important;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12) !important;
        }
        .leaflet-popup-tip { background: #FCFAF8 !important; }
        .leaflet-container { font-family: 'Outfit', sans-serif !important; background: #F2EFE9 !important; }
      `}</style>
    </section>
  );
}
